/**
 * Luxire Product Recommendation Engine v2
 *
 * Scores products using:
 *   - Color wheel harmonies (complementary, analogous, triadic, monochromatic)
 *   - Formality proximity (fabric + style formality)
 *   - Material & weave compatibility
 *   - Mill / origin affinity
 *   - Season alignment
 *   - Price bracket consistency
 *   - Popularity (reviews, ratings)
 *
 * Lazy-loaded via IntersectionObserver when the section enters viewport.
 * Parses Luxire tag format: luxire:category:value
 */

(function () {
  'use strict';

  /* ── Color Wheel Harmonies ── */
  var COLOR_HARMONIES = {
    blue:         { complement: ['orange','earth'],    analogous: ['blue-green','purple'], triadic: ['red','yellow'] },
    'blue-green': { complement: ['orange','red'],      analogous: ['blue','green'],        triadic: ['purple','earth'] },
    green:        { complement: ['red','pink'],         analogous: ['blue-green','yellow'], triadic: ['orange','purple'] },
    yellow:       { complement: ['purple'],             analogous: ['green','orange'],      triadic: ['blue','red'] },
    orange:       { complement: ['blue'],               analogous: ['yellow','red'],        triadic: ['green','purple'] },
    red:          { complement: ['green'],              analogous: ['orange','pink'],       triadic: ['blue','yellow'] },
    pink:         { complement: ['green'],              analogous: ['red','purple'],        triadic: ['blue-green','yellow'] },
    purple:       { complement: ['yellow','earth'],     analogous: ['blue','pink'],         triadic: ['orange','green'] },
    neutral:      { complement: [],                     analogous: [],                      triadic: [] },
    earth:        { complement: ['blue','purple'],      analogous: ['neutral','green'],     triadic: ['orange','red'] },
  };

  /* ── Cross-Category Pairing Map ── */
  var COMPLEMENT_TYPES = {
    PA: ['SH','JK','TI','PQ','SU','OS'],
    SH: ['PA','JK','TI','PQ','SU'],
    JK: ['PA','SH','TI','PQ'],
    SU: ['SH','TI','PQ'],
    TI: ['SH','PQ','JK','SU'],
    PQ: ['SH','TI','JK','SU'],
    OW: ['SH','PA','JK','SU'],
    PO: ['PA'],
    OS: ['PA','SH'],
    SC: ['SH','JK','SU','OW'],
  };

  /* ── Scoring Weights ── */
  var W = {
    colorMatch:     25,
    colorHarmony:   18,
    formalityMatch: 22,
    formalityClose: 10,
    materialMatch:  12,
    weaveMatch:      6,
    styleMatch:      8,
    originMatch:     5,
    seasonMatch:    10,
    priceClose:      6,
    popularity:     10,
    ratingBonus:     8,
  };

  /* ── Color-to-Family Map ── */
  var CF_MAP = {
    'navy':'blue','blue':'blue','light blue':'blue','sky blue':'blue',
    'baby blue':'blue','azure':'blue','indigo':'blue','cobalt':'blue',
    'sapphire':'blue','royal blue':'blue',
    'teal':'blue-green','turquoise':'blue-green','aqua':'blue-green',
    'white':'neutral','cream':'neutral','ecru':'neutral','ivory':'neutral',
    'grey':'neutral','gray':'neutral','charcoal':'neutral','black':'neutral',
    'silver':'neutral','anthracite':'neutral','slate':'neutral',
    'brown':'earth','tan':'earth','beige':'earth','khaki':'earth',
    'camel':'earth','tobacco':'earth','sand':'earth','taupe':'earth',
    'coffee':'earth','fawn':'earth','chocolate':'earth',
    'olive':'green','green':'green','sage':'green','forest':'green',
    'moss':'green','emerald':'green','mint':'green',
    'red':'red','burgundy':'red','wine':'red','maroon':'red','crimson':'red','brick':'red',
    'rust':'orange','orange':'orange','terracotta':'orange','copper':'orange','apricot':'orange',
    'mustard':'yellow','yellow':'yellow','gold':'yellow','saffron':'yellow',
    'pink':'pink','salmon':'pink','rose':'pink','coral':'pink','blush':'pink',
    'purple':'purple','lavender':'purple','plum':'purple','lilac':'purple','mauve':'purple',
  };

  var catalog = null;
  var cdnBase = '';

  /* ── Load Catalog ── */
  function loadCatalog() {
    if (catalog) return Promise.resolve(catalog);

    var el = document.querySelector('[data-catalog-url]');
    var url = el ? el.dataset.catalogUrl : null;

    if (!url) {
      var scriptEl = document.querySelector('script[src*="product-recommendations"]');
      if (scriptEl) url = scriptEl.src.replace('product-recommendations.js', 'product-catalog.json');
    }
    if (!url) {
      url = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') +
            'cdn/shop/files/product-catalog.json';
    }

    return fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('Catalog fetch failed: ' + r.status);
        return r.json();
      })
      .then(function (data) {
        catalog = data.p;
        cdnBase = data.cdn || '';
        return catalog;
      });
  }

  /* ── Expand Shortened Image URL ── */
  function expandImg(shortUrl) {
    if (!shortUrl) return '';
    if (shortUrl.charAt(0) === '~' && shortUrl.charAt(1) === '/') {
      return cdnBase + shortUrl.slice(2) + (shortUrl.indexOf('?') === -1 ? '?width=300' : '&width=300');
    }
    return shortUrl + (shortUrl.indexOf('?') === -1 ? '?width=300' : '&width=300');
  }

  /* ── Array helper: has overlap ── */
  function hasOverlap(a, b) {
    if (!a || !b) return false;
    for (var i = 0; i < a.length; i++) {
      var ai = a[i].toLowerCase();
      for (var j = 0; j < b.length; j++) {
        if (ai === b[j].toLowerCase()) return true;
      }
    }
    return false;
  }

  /* ── "More Like This" Scoring ── */
  function scoreSimilar(src, cand) {
    var s = 0;
    if (src.cf && cand.cf && src.cf === cand.cf) s += W.colorMatch;
    var fDiff = Math.abs((src.f || 5) - (cand.f || 5));
    if (fDiff <= 1) s += W.formalityMatch;
    else if (fDiff <= 2) s += W.formalityClose;
    if (hasOverlap(src.m, cand.m)) s += W.materialMatch;
    if (src.w && cand.w && src.w.toLowerCase() === cand.w.toLowerCase()) s += W.weaveMatch;
    if (hasOverlap(src.s, cand.s)) s += W.styleMatch;
    if (src.o && cand.o && src.o.toLowerCase() === cand.o.toLowerCase()) s += W.originMatch;
    if (src.sn && cand.sn && src.sn === cand.sn) s += W.seasonMatch;
    if (src.p > 0 && cand.p > 0) {
      var pRatio = Math.min(src.p, cand.p) / Math.max(src.p, cand.p);
      if (pRatio > 0.7) s += W.priceClose;
    }
    if (cand.rc && cand.rc > 3) s += Math.min(W.popularity, Math.round(cand.rc * 0.5));
    if (cand.r && cand.r >= 4.5) s += W.ratingBonus;
    return s;
  }

  /* ── "Complete the Look" Scoring ── */
  function scoreComplement(src, cand) {
    var s = 0;
    if (src.cf && cand.cf) {
      if (cand.cf === 'neutral' || src.cf === 'neutral') {
        s += W.colorHarmony + 3;
      } else {
        var h = COLOR_HARMONIES[src.cf];
        if (h) {
          if (h.complement.indexOf(cand.cf) !== -1) s += W.colorHarmony + 5;
          else if (h.analogous.indexOf(cand.cf) !== -1) s += W.colorHarmony;
          else if (h.triadic.indexOf(cand.cf) !== -1) s += W.colorHarmony - 3;
          else if (src.cf === cand.cf) s += W.colorMatch - 8;
        }
      }
    }
    var fDiff = Math.abs((src.f || 5) - (cand.f || 5));
    if (fDiff <= 1) s += W.formalityMatch;
    else if (fDiff <= 2) s += W.formalityClose;
    if (src.sn && cand.sn && src.sn === cand.sn) s += W.seasonMatch;
    if (src.p > 0 && cand.p > 0) {
      var pRatio = Math.min(src.p, cand.p) / Math.max(src.p, cand.p);
      if (pRatio > 0.5) s += W.priceClose;
    }
    if (cand.rc && cand.rc > 3) s += Math.min(W.popularity, Math.round(cand.rc * 0.5));
    if (cand.r && cand.r >= 4.5) s += W.ratingBonus;
    return s;
  }

  /* ── Main Recommendation Engine ── */
  function getRecommendations(source, options) {
    options = options || {};
    var maxSimilar = options.maxSimilar || 8;
    var maxComplement = options.maxComplement || 8;
    if (!catalog || !source) return { similar: [], complement: [] };

    /* More Like This — same type */
    var sameType = [];
    for (var i = 0; i < catalog.length; i++) {
      if (catalog[i].y === source.y && catalog[i].h !== source.h) sameType.push(catalog[i]);
    }
    var simScored = [];
    for (var a = 0; a < sameType.length; a++) {
      simScored.push({ p: sameType[a], sc: scoreSimilar(source, sameType[a]) });
    }
    simScored.sort(function (x, y) { return y.sc - x.sc; });
    var similar = [];
    for (var b = 0; b < Math.min(maxSimilar, simScored.length); b++) similar.push(simScored[b].p);

    /* Complete the Look — cross-category */
    var compTypes = COMPLEMENT_TYPES[source.y] || [];
    var others = [];
    for (var c = 0; c < catalog.length; c++) {
      if (compTypes.indexOf(catalog[c].y) !== -1 && catalog[c].h !== source.h) others.push(catalog[c]);
    }
    var compScored = [];
    for (var d = 0; d < others.length; d++) {
      compScored.push({ p: others[d], sc: scoreComplement(source, others[d]) });
    }
    compScored.sort(function (x, y) { return y.sc - x.sc; });

    /* Type diversity: max 3 per type */
    var complement = [];
    var typeCounts = {};
    for (var e = 0; e < compScored.length && complement.length < maxComplement; e++) {
      var cp = compScored[e].p;
      typeCounts[cp.y] = (typeCounts[cp.y] || 0) + 1;
      if (typeCounts[cp.y] <= 3) complement.push(cp);
    }

    return { similar: similar, complement: complement };
  }

  /* ── Render Product Card ── */
  function renderCard(product) {
    var img = expandImg(product.i);
    var url = '/products/' + product.h;
    var price = product.p ? '$' + Number(product.p).toFixed(2) : '';
    var name = product.n || '';
    var rating = product.r || 0;
    var rc = product.rc || 0;
    var ratingHtml = '';
    if (rating && rc) {
      var stars = '';
      for (var i = 1; i <= 5; i++) {
        stars += i <= Math.round(rating)
          ? '<span class="rec-card__star rec-card__star--filled">\u2605</span>'
          : '<span class="rec-card__star">\u2606</span>';
      }
      ratingHtml = '<div class="rec-card__rating">' + stars +
        '<span class="rec-card__review-count">(' + rc + ')</span></div>';
    }
    return '<a href="' + url + '" class="rec-card" data-handle="' + product.h + '">' +
      '<div class="rec-card__img-wrap">' +
        (img ? '<img src="' + img + '" alt="' + name + '" loading="lazy" width="300">' : '') +
      '</div>' +
      '<div class="rec-card__info">' +
        '<span class="rec-card__name">' + name + '</span>' +
        ratingHtml +
        '<span class="rec-card__price">' + price + '</span>' +
      '</div></a>';
  }

  /* ── Render Sections ── */
  function renderRecommendations(container, results) {
    if (!container) return;
    var html = '';
    if (results.similar.length) {
      html += '<div class="rec-section" data-rec="similar">' +
        '<h3 class="rec-section__title">More Like This</h3>' +
        buildScrollWrap(results.similar) +
        '</div>';
    }
    if (results.complement.length) {
      html += '<div class="rec-section" data-rec="complement">' +
        '<h3 class="rec-section__title">Complete the Look</h3>' +
        buildScrollWrap(results.complement) +
        '</div>';
    }
    if (!html) html = '<p class="rec-section__empty">Explore our full collection for more options.</p>';
    container.innerHTML = html;
    initScrollArrows(container);
  }

  function buildScrollWrap(items) {
    var cards = '';
    for (var i = 0; i < items.length; i++) cards += renderCard(items[i]);
    return '<div class="rec-section__scroll-wrap" data-rec-scroll-wrap>' +
      '<div class="rec-section__fade rec-section__fade--left" data-rec-fade="left"></div>' +
      '<div class="rec-section__fade rec-section__fade--right is-visible" data-rec-fade="right"></div>' +
      '<button class="rec-section__arrow rec-section__arrow--left" data-rec-arrow="left" type="button" aria-label="Scroll left">' +
        '<svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="11 4 6 9 11 14"/></svg>' +
      '</button>' +
      '<button class="rec-section__arrow rec-section__arrow--right" data-rec-arrow="right" type="button" aria-label="Scroll right">' +
        '<svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="7 4 12 9 7 14"/></svg>' +
      '</button>' +
      '<div class="rec-section__scroll" data-rec-scroll>' + cards + '</div>' +
    '</div>';
  }

  /* ── Scroll Arrows for Recommendation Carousels ── */
  function initScrollArrows(container) {
    container.querySelectorAll('[data-rec-scroll-wrap]').forEach(function(wrap) {
      var scroll = wrap.querySelector('[data-rec-scroll]');
      var arrowL = wrap.querySelector('[data-rec-arrow="left"]');
      var arrowR = wrap.querySelector('[data-rec-arrow="right"]');
      var fadeL = wrap.querySelector('[data-rec-fade="left"]');
      var fadeR = wrap.querySelector('[data-rec-fade="right"]');
      if (!scroll) return;

      function update() {
        var sl = scroll.scrollLeft;
        var maxScroll = scroll.scrollWidth - scroll.clientWidth;
        var atLeft = sl < 5;
        var atRight = sl >= maxScroll - 5;

        if (arrowL) arrowL.classList.toggle('is-visible', !atLeft);
        if (arrowR) arrowR.classList.toggle('is-visible', !atRight);
        if (fadeL) fadeL.classList.toggle('is-visible', !atLeft);
        if (fadeR) fadeR.classList.toggle('is-visible', !atRight);
      }

      scroll.addEventListener('scroll', update, { passive: true });
      if (arrowL) arrowL.addEventListener('click', function() {
        scroll.scrollBy({ left: -200, behavior: 'smooth' });
      });
      if (arrowR) arrowR.addEventListener('click', function() {
        scroll.scrollBy({ left: 200, behavior: 'smooth' });
      });

      /* Initial state */
      setTimeout(update, 100);
    });
  }

  /* ── Extract Current Product from Page Tags ── */
  function extractCurrentProduct() {
    var recEl = document.querySelector('[data-product-recommendations]');
    var ds = recEl ? recEl.dataset : {};
    var productData = {};
    var jsonEl = document.querySelector('[data-product-json]');
    if (jsonEl) { try { productData = JSON.parse(jsonEl.textContent); } catch (e) {} }

    /* Collect and normalize tags */
    var rawTags = productData.tags || ds.productTags || '';
    var tags = (Array.isArray(rawTags) ? rawTags : rawTags.split(',')).map(function (t) {
      return t.trim().toLowerCase();
    });

    /* Extract values for luxire:category:value tags */
    function luxTags(category) {
      var prefix = 'luxire:' + category + ':';
      var vals = [];
      for (var i = 0; i < tags.length; i++) {
        if (tags[i].indexOf(prefix) === 0) {
          var v = tags[i].slice(prefix.length).trim();
          if (v && v !== 'unknown') vals.push(v);
        }
      }
      return vals;
    }

    function cap(str) { return str ? str.charAt(0).toUpperCase() + str.slice(1) : ''; }

    /* Colors */
    var colors = luxTags('color');
    var colorFamily = '';
    if (colors.length) {
      colorFamily = CF_MAP[colors[0]] || '';
      if (!colorFamily) {
        var cfKeys = Object.keys(CF_MAP);
        for (var k = 0; k < cfKeys.length; k++) {
          if (colors[0].indexOf(cfKeys[k]) !== -1) { colorFamily = CF_MAP[cfKeys[k]]; break; }
        }
      }
    }

    /* Formality */
    var fVals = luxTags('formality');
    var ffVals = luxTags('formality-fabric');
    var fsVals = luxTags('formality-style');
    var formality = 5;
    if (fVals.length) formality = parseInt(fVals[0], 10) || 5;
    else if (ffVals.length && fsVals.length) formality = Math.round((parseInt(ffVals[0], 10) + parseInt(fsVals[0], 10)) / 2) || 5;
    else if (ffVals.length) formality = parseInt(ffVals[0], 10) || 5;
    else if (ds.productFormality) formality = parseInt(ds.productFormality, 10) || 5;

    /* Product type → code */
    var typeStr = (productData.type || ds.productType || '').toLowerCase();
    var typeMap = { pants:'PA', shirt:'SH', shirts:'SH', jackets:'JK', jacket:'JK',
      suits:'SU', suit:'SU', outerwear:'OW', polo:'PO', ties:'TI', tie:'TI',
      'pocket square':'PQ', overshirt:'OS', scarf:'SC' };
    var typeCode = typeMap[typeStr] || 'PA';
    var typeTag = luxTags('type');
    if (typeTag.length && typeCode === 'PA') {
      var ttMap = { shirt:'SH', jacket:'JK', suit:'SU', pant:'PA', pants:'PA',
        tie:'TI', 'pocket-square':'PQ', polo:'PO', overshirt:'OS', scarf:'SC', outerwear:'OW' };
      typeCode = ttMap[typeTag[0]] || typeCode;
    }

    /* Price */
    var price = 0;
    if (productData.variants && productData.variants[0]) {
      price = parseFloat(productData.variants[0].price) / 100 || 0;
    } else if (ds.productPrice) {
      price = parseFloat(ds.productPrice) || 0;
    }

    return {
      h:  productData.handle || ds.productHandle || '',
      y:  typeCode,
      cf: colorFamily,
      c:  colors.map(cap),
      f:  formality,
      m:  (luxTags('composition').length ? luxTags('composition') : luxTags('material')).map(cap),
      w:  cap(luxTags('weave')[0] || ''),
      s:  luxTags('pattern').map(cap),
      o:  cap(luxTags('mill')[0] || luxTags('origin')[0] || ''),
      sn: cap(luxTags('season')[0] || ''),
      p:  price,
    };
  }

  /* ── Merge page data with catalog (catalog fills gaps) ── */
  function mergeWithCatalog(source) {
    if (!catalog || !source.h) return source;
    for (var i = 0; i < catalog.length; i++) {
      if (catalog[i].h === source.h) {
        var cat = catalog[i];
        if (!source.cf && cat.cf) source.cf = cat.cf;
        if ((!source.m || !source.m.length) && cat.m) source.m = cat.m;
        if (!source.w && cat.w) source.w = cat.w;
        if ((!source.s || !source.s.length) && cat.s) source.s = cat.s;
        if (!source.o && cat.o) source.o = cat.o;
        if (!source.sn && cat.sn) source.sn = cat.sn;
        if (source.f === 5 && cat.f) source.f = cat.f;
        if (!source.p && cat.p) source.p = cat.p;
        break;
      }
    }
    return source;
  }

  /* ── Initialize (Lazy — IntersectionObserver) ── */
  function init() {
    var container = document.querySelector('[data-product-recommendations]');
    if (!container) return;
    var observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          observer.disconnect();
          loadCatalog()
            .then(function () {
              var source = extractCurrentProduct();
              source = mergeWithCatalog(source);
              var results = getRecommendations(source);
              renderRecommendations(container, results);
            })
            .catch(function (err) {
              console.warn('Luxire recommendations:', err);
              container.innerHTML = '';
            });
          break;
        }
      }
    }, { rootMargin: '300px' });
    observer.observe(container);

    /* Also init arrows on pre-rendered (liquid template) scroll wraps */
    initScrollArrows(container);
  }

  /* ── Public API ── */
  window.LuxireRecommendations = {
    init: init,
    getRecommendations: getRecommendations,
    loadCatalog: loadCatalog,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
